/**
 * Export utilities for handling file downloads and error management.
 *
 * Provides helpers for downloading PDF and CSV files from blob responses.
 */

/**
 * Download a file from a blob response.
 *
 * @param blob - File blob
 * @param filename - Desired filename
 */
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate filename for progress report.
 *
 * @param type - File type ('pdf' or 'csv')
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @returns Formatted filename
 */
export const generateExportFilename = (
  type: 'pdf' | 'csv',
  startDate: string,
  endDate: string
): string => {
  const timestamp = new Date().toISOString().split('T')[0];
  return `wellness_${type === 'pdf' ? 'report' : 'data'}_${startDate}_to_${endDate}_${timestamp}.${type}`;
};

/**
 * Format date for export API (YYYY-MM-DD).
 *
 * @param date - Date object or date string
 * @returns ISO date string (YYYY-MM-DD)
 */
export const formatDateForExport = (date: Date | string): string => {
  if (typeof date === 'string') {
    return date.split('T')[0]; // Extract date part from ISO string
  }
  return date.toISOString().split('T')[0];
};

/**
 * Calculate date range from days parameter.
 *
 * @param days - Number of days to look back
 * @returns Object with start_date and end_date strings
 */
export const calculateDateRange = (days: number): { start_date: string; end_date: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);

  return {
    start_date: formatDateForExport(start),
    end_date: formatDateForExport(end)
  };
};

/**
 * Handle export errors with user-friendly messages.
 *
 * @param error - Error object
 * @returns User-friendly error message
 */
export const handleExportError = (error: any): string => {
  if (error.response) {
    const status = error.response.status;
    const detail = error.response.data?.detail;

    if (status === 404) {
      return detail || 'No data found for the selected date range.';
    } else if (status === 400) {
      return detail || 'Invalid request. Please check your date range.';
    } else if (status === 500) {
      return 'Server error while generating export. Please try again.';
    }
  }

  if (error.message?.includes('Network Error')) {
    return 'Network error. Please check your connection and try again.';
  }

  return 'Failed to export data. Please try again.';
};

/**
 * Validate date range for export.
 *
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Validation result with error message if invalid
 */
export const validateDateRange = (
  startDate: string,
  endDate: string
): { valid: boolean; error?: string } => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }

  if (start > end) {
    return { valid: false, error: 'Start date must be before end date' };
  }

  const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  if (daysDiff > 90) {
    return { valid: false, error: 'Date range cannot exceed 90 days' };
  }

  if (daysDiff < 1) {
    return { valid: false, error: 'Date range must be at least 1 day' };
  }

  return { valid: true };
};
